// Event-projection helpers. Converts a successful
// `CaptureMutationResult` into the authoritative `CaptureEvent` that
// the store listens for, so the adapter's worker loop (in
// `capture-client.ts`) stays focused on the queue state machine.

import type {
  CaptureEvent,
  CaptureMutation,
  CaptureMutationResult,
} from './capture-client.types'

export function eventForResult(
  mutation: CaptureMutation,
  result: CaptureMutationResult,
): CaptureEvent {
  switch (result.kind) {
    case 'updateItem': {
      const updateMutation = mutation as Extract<
        CaptureMutation,
        { kind: 'updateItem' }
      >
      return {
        kind: 'item-changed',
        itemId: updateMutation.itemId,
        item: result.item,
      }
    }
    case 'createItem': {
      const row = result.item as { id?: string } | null
      const itemId =
        row !== null && typeof row?.id === 'string' ? row.id : ''
      return { kind: 'item-changed', itemId, item: result.item }
    }
    case 'createSpace': {
      const createMutation = mutation as Extract<
        CaptureMutation,
        { kind: 'createSpace' }
      >
      return {
        kind: 'space-created',
        sessionId: createMutation.sessionId,
        space: result.space,
        items: result.items,
      }
    }
    case 'createSession': {
      const row = result.session as { id?: string } | null
      const sessionId =
        row !== null && typeof row?.id === 'string' ? row.id : ''
      return {
        kind: 'session-created',
        sessionId,
        session: result.session,
      }
    }
    case 'completeSession': {
      const completeMutation = mutation as Extract<
        CaptureMutation,
        { kind: 'completeSession' }
      >
      return {
        kind: 'session-completed',
        sessionId: completeMutation.sessionId,
        session: result.session,
      }
    }
    case 'reopenSession': {
      const reopenMutation = mutation as Extract<
        CaptureMutation,
        { kind: 'reopenSession' }
      >
      return {
        kind: 'session-reopened',
        sessionId: reopenMutation.sessionId,
        session: result.session,
      }
    }
    case 'cancelSession': {
      return {
        kind: 'session-cancelled',
        sessionId: result.sessionId,
        buildingId: result.buildingId,
        buildingStatus: result.buildingStatus,
      }
    }
    case 'deleteSpace': {
      return {
        kind: 'space-deleted',
        spaceId: result.spaceId,
        buildingId: result.buildingId,
        buildingStatus: result.buildingStatus,
      }
    }
    case 'updateSpaceAlias': {
      return {
        kind: 'alias-updated',
        spaceId: result.spaceId,
        alias: result.alias,
      }
    }
    case 'createFieldAddedVariant': {
      return {
        kind: 'field-added-variant-created',
        assetType: result.assetType,
      }
    }
  }
}

// 401 is intentionally excluded — auth expiry is a recoverable condition
// handled separately in capture-client.ts §handleFailure (20-ui §8.6a).
export function isPermanentClientError(status: number): boolean {
  return status === 400 || status === 403 || status === 404
}

export function extractAuthoritative(body: unknown): unknown {
  if (body === null || typeof body !== 'object') return null
  const details = (body as Record<string, unknown>).details
  if (details === null || typeof details !== 'object') return null
  const authoritative = (details as Record<string, unknown>).authoritative
  return authoritative ?? null
}

export function extractSyncVersion(row: unknown): number | null {
  if (row === null || typeof row !== 'object') return null
  const value = (row as Record<string, unknown>).syncVersion
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  return value
}

export function extractRetryAfterMs(body: unknown): number | null {
  if (body === null || typeof body !== 'object') return null
  const details = (body as Record<string, unknown>).details
  if (details === null || typeof details !== 'object') return null
  const retryAfter = (details as Record<string, unknown>).retryAfter
  if (typeof retryAfter === 'number' && Number.isFinite(retryAfter)) {
    return Math.max(0, retryAfter * 1_000)
  }
  return null
}
